/**
 * JS crash reporter (scope-system §5): the FIRST uncaught error or rejected
 * promise per session posts a compact problem report to the feedback API —
 * page, message, stack — so live breakage reaches the owner desk (and the
 * hub) without the user lifting a finger. One per session: a render loop
 * must not flood the inbox. Best-effort by design.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
let sent = false;

// Errors that are NOT Vault's and must never be filed as a Vault crash: code
// injected by a BROWSER EXTENSION (e.g. a location-spoofing extension throwing
// "_e is not defined" into our global handlers), the opaque cross-origin
// "Script error.", and the benign ResizeObserver-loop notice. We also don't
// consume the one-per-session slot on these, so a REAL Vault crash still reports.
function isNoise(message, stack) {
  const s = `${message || ''} ${stack || ''}`;
  if (/(?:chrome|moz|safari(?:-web)?)-extension:\/\//i.test(s)) return true;
  if (/^\s*Script error\.?\s*$/i.test(message || '')) return true;
  if (/ResizeObserver loop/i.test(s)) return true;
  return false;
}

function report(message, stack) {
  if (sent || isNoise(message, stack)) return;
  sent = true;
  try {
    const fd = new FormData();
    fd.append('kind', 'problem');
    fd.append('capture', 'dom');
    fd.append('surface', 'frontend');
    fd.append('page_url', window.location.href);
    fd.append('description',
              `JS CRASH (auto-report)\n${message}\n\n${(stack || '').slice(0, 4000)}`);
    fd.append('events', '[]');
    fd.append('duration_ms', '0');
    const token = localStorage.getItem('vault_access')
      || sessionStorage.getItem('vault_access') || '';
    fetch('/api/feedback/journeys/', {
      method: 'POST', body: fd,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => {});
  } catch {
    /* the reporter itself must never throw */
  }
}

export function installCrashReporter() {
  window.addEventListener('error', (e) => {
    report(String(e.message || e.error || 'unknown error'),
           e.error?.stack || `${e.filename}:${e.lineno}`);
  });
  window.addEventListener('unhandledrejection', (e) => {
    report(`Unhandled rejection: ${String(e.reason?.message || e.reason)}`,
           e.reason?.stack || '');
  });
}
